import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { DragDropData } from '@/data/lessons';

interface DragDropQuestionProps {
  question: string;
  data: DragDropData;
  onAnswer: (isCorrect: boolean) => void;
  answered: boolean;
}

function FootballFieldBg() {
  return (
    <>
      {[20, 35, 50, 65, 80].map(y => (
        <line key={y} x1="5" y1={y} x2="155" y2={y} stroke="hsl(210, 40%, 22%)" strokeWidth="0.3" strokeDasharray="3,2" />
      ))}
      <line x1="5" y1="50" x2="155" y2="50" stroke="hsl(48, 96%, 53%)" strokeWidth="0.8" />
      <text x="5" y="47" fill="hsl(48, 96%, 53%)" fontSize="3.5" fontFamily="Kanit" fontWeight="700">LOS</text>
    </>
  );
}

function SoccerFieldBg() {
  return (
    <>
      <rect x="5" y="5" width="150" height="90" fill="none" stroke="hsl(140, 30%, 35%)" strokeWidth="0.5" />
      <line x1="5" y1="50" x2="155" y2="50" stroke="hsl(140, 30%, 35%)" strokeWidth="0.4" />
      <circle cx="80" cy="50" r="10" fill="none" stroke="hsl(140, 30%, 35%)" strokeWidth="0.4" />
      <circle cx="80" cy="50" r="1" fill="hsl(140, 30%, 35%)" />
      <rect x="40" y="5" width="80" height="16" fill="none" stroke="hsl(140, 30%, 35%)" strokeWidth="0.4" />
      <rect x="40" y="79" width="80" height="16" fill="none" stroke="hsl(140, 30%, 35%)" strokeWidth="0.4" />
      <rect x="55" y="5" width="50" height="7" fill="none" stroke="hsl(140, 30%, 35%)" strokeWidth="0.3" />
      <rect x="55" y="88" width="50" height="7" fill="none" stroke="hsl(140, 30%, 35%)" strokeWidth="0.3" />
      <rect x="65" y="2" width="30" height="3" fill="none" stroke="hsl(140, 40%, 50%)" strokeWidth="0.5" />
      <rect x="65" y="95" width="30" height="3" fill="none" stroke="hsl(140, 40%, 50%)" strokeWidth="0.5" />
    </>
  );
}

function DropSlot({
  slot,
  piece,
  status,
  isTargetable,
  onClick,
  submitted,
  justPlaced,
}: {
  slot: { id: string; label: string; x: number; y: number };
  piece: string | null;
  status: 'neutral' | 'correct' | 'incorrect';
  isTargetable: boolean;
  onClick: () => void;
  submitted: boolean;
  justPlaced: boolean;
}) {
  const strokeColor = status === 'correct'
    ? 'hsl(142, 71%, 45%)'
    : status === 'incorrect'
      ? 'hsl(0, 84%, 60%)'
      : isTargetable
        ? 'hsl(48, 96%, 60%)'
        : piece
          ? 'hsl(210, 60%, 55%)'
          : 'hsl(0, 0%, 40%)';

  return (
    <g
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      role="button"
      tabIndex={submitted ? -1 : 0}
      aria-label={`${slot.label} slot${piece ? `, holds ${piece}` : ', empty'}${isTargetable ? ', press to place selected piece' : ''}`}
      aria-disabled={submitted}
      className="cursor-pointer outline-none focus-visible:[&>circle.hit]:stroke-primary"
    >
      {/* Generous transparent hit target so taps near the slot always register */}
      <circle className="hit" cx={slot.x} cy={slot.y} r="8.5" fill="transparent" stroke="transparent" strokeWidth="1" />
      {/* Pulse ring when targetable */}
      {isTargetable && !submitted && (
        <circle
          cx={slot.x}
          cy={slot.y}
          r="7.5"
          fill="none"
          stroke="hsl(48, 96%, 53%)"
          strokeWidth="0.4"
          opacity="0.5"
          pointerEvents="none"
        >
          <animate attributeName="r" values="7;9;7" dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;0.15;0.5" dur="1.5s" repeatCount="indefinite" />
        </circle>
      )}

      {/* Main circle */}
      <circle
        cx={slot.x}
        cy={slot.y}
        r="6"
        fill={piece ? 'hsl(var(--primary) / 0.25)' : 'hsl(0, 0%, 12%)'}
        stroke={strokeColor}
        strokeWidth={isTargetable ? '1' : '0.7'}
        strokeDasharray={piece ? undefined : '2,1.5'}
        pointerEvents="none"
      >
        {justPlaced && (
          <animate attributeName="r" values="6;7.5;6" dur="0.3s" repeatCount="1" />
        )}
      </circle>

      {/* Label */}
      <text
        x={slot.x}
        y={slot.y + 0.5}
        textAnchor="middle"
        dominantBaseline="central"
        fill="hsl(0, 0%, 92%)"
        fontSize="3.5"
        fontFamily="Kanit"
        fontWeight="700"
        pointerEvents="none"
      >
        {piece || slot.label}
      </text>

      {/* Result badges */}
      {submitted && status === 'correct' && (
        <circle cx={slot.x + 5.5} cy={slot.y - 5} r="2.2" fill="hsl(142, 71%, 45%)" pointerEvents="none" />
      )}
      {submitted && status === 'incorrect' && (
        <circle cx={slot.x + 5.5} cy={slot.y - 5} r="2.2" fill="hsl(0, 84%, 60%)" pointerEvents="none" />
      )}
    </g>
  );
}

export function DragDropQuestion({ question, data, onAnswer, answered }: DragDropQuestionProps) {
  const [placements, setPlacements] = useState<Record<string, string | null>>(() => {
    const init: Record<string, string | null> = {};
    data.slots.forEach(s => { init[s.id] = null; });
    return init;
  });
  const [selectedPiece, setSelectedPiece] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [justPlacedSlot, setJustPlacedSlot] = useState<string | null>(null);

  const usedPieces = Object.values(placements).filter(Boolean) as string[];
  const availablePieces = (() => {
    const usedCounts: Record<string, number> = {};
    usedPieces.forEach(p => { usedCounts[p] = (usedCounts[p] || 0) + 1; });
    const result: string[] = [];
    const seenCounts: Record<string, number> = {};
    data.pieces.forEach(p => {
      seenCounts[p] = (seenCounts[p] || 0) + 1;
      if (seenCounts[p] > (usedCounts[p] || 0)) {
        result.push(p);
      }
    });
    return result;
  })();
  const allFilled = Object.values(placements).every(v => v !== null);

  const handleSlotTap = useCallback((slotId: string) => {
    if (submitted) return;

    const currentPiece = placements[slotId];
    if (currentPiece && !selectedPiece) {
      setPlacements(prev => ({ ...prev, [slotId]: null }));
      return;
    }

    if (selectedPiece) {
      setPlacements(prev => ({ ...prev, [slotId]: selectedPiece }));
      setSelectedPiece(null);
      setJustPlacedSlot(slotId);
      setTimeout(() => setJustPlacedSlot(null), 350);
    }
  }, [selectedPiece, placements, submitted]);

  const handlePieceTap = useCallback((piece: string) => {
    if (submitted) return;
    setSelectedPiece(prev => prev === piece ? null : piece);
  }, [submitted]);

  const handleSubmit = useCallback(() => {
    setSubmitted(true);
    const isCorrect = data.slots.every(
      slot => placements[slot.id] === data.correctMapping[slot.id]
    );
    onAnswer(isCorrect);
  }, [data, placements, onAnswer]);

  const getSlotStatus = (slotId: string) => {
    if (!submitted) return 'neutral' as const;
    return placements[slotId] === data.correctMapping[slotId] ? 'correct' as const : 'incorrect' as const;
  };

  const sport = data.sport || 'football';

  return (
    <div className="w-full space-y-5">
      <div className="text-center">
        <span className="inline-block bg-accent/20 text-accent-foreground font-display font-bold text-xs px-3 py-1 rounded-full mb-3">
          🎯 DRAG & DROP
        </span>
      </div>

      <p className="font-body font-semibold text-lg text-center leading-snug">
        {question}
      </p>

      {/* Field with drop slots */}
      <div className="bg-secondary rounded-xl p-3 relative overflow-hidden">
        <svg
          viewBox="0 0 160 100"
          preserveAspectRatio="xMidYMid meet"
          className="w-full"
          style={{ minHeight: 240 }}
          role="group"
          aria-label="Field positions"
        >
          {sport === 'football' && <FootballFieldBg />}
          {sport === 'soccer' && <SoccerFieldBg />}

          {/* Drop slots */}
          {data.slots.map(slot => (
            <DropSlot
              key={slot.id}
              slot={slot}
              piece={placements[slot.id]}
              status={getSlotStatus(slot.id)}
              isTargetable={!!selectedPiece && !placements[slot.id]}
              onClick={() => handleSlotTap(slot.id)}
              submitted={submitted}
              justPlaced={justPlacedSlot === slot.id}
            />
          ))}
        </svg>
      </div>

      {/* Piece bank */}
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground font-display font-bold uppercase text-center">
          {submitted ? 'Correct Answers' : 'Tap a position, then tap where to place it'}
        </p>
        <div className="flex flex-wrap gap-2 justify-center">
          {(submitted ? data.pieces : availablePieces).map((piece, idx) => {
            const isSelected = selectedPiece === piece && !submitted;
            const isCorrectlyPlaced = submitted && Object.values(data.correctMapping).includes(piece);

            return (
              <motion.button
                key={`${piece}-${idx}`}
                onClick={() => handlePieceTap(piece)}
                disabled={submitted}
                aria-pressed={isSelected}
                aria-label={`${piece}${isSelected ? ', selected — now choose a field slot' : ''}`}
                whileTap={!submitted ? { scale: 0.95 } : {}}
                className={`px-3 py-2 rounded-lg border-2 font-display font-bold text-sm transition-all flex items-center gap-1.5 ${
                  submitted
                    ? isCorrectlyPlaced
                      ? 'bg-success/10 border-success text-success'
                      : 'bg-muted border-border text-muted-foreground'
                    : isSelected
                      ? 'bg-primary/20 border-primary text-primary scale-105 ring-2 ring-primary/40 ring-offset-1 ring-offset-background'
                      : 'bg-card border-border hover:border-primary/50 text-foreground'
                }`}
              >
                <GripVertical className="w-3 h-3 text-muted-foreground" />
                {piece}
              </motion.button>
            );
          })}
        </div>
      </div>

      {!submitted && (
        <Button
          onClick={handleSubmit}
          disabled={!allFilled}
          className="w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl disabled:opacity-50"
        >
          Check Answer ✅
        </Button>
      )}
    </div>
  );
}
